import styled, { css, keyframes } from 'styled-components';

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export const FloatingButton = styled.button`
  ${({ theme }) => css`
    width: 52px;
    height: 52px;
    border-radius: 50%;
    border: none;
    cursor: pointer;

    background: ${theme.colors.primary};
    color: ${theme.colors.onPrimary};

    display: flex;
    align-items: center;
    justify-content: center;

    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    transition:
      transform 0.15s ease,
      box-shadow 0.15s ease,
      background 0.15s ease;

    &:hover {
      background: ${theme.colors.primaryContainer};
      color: ${theme.colors.onPrimaryContainer};
      transform: scale(1.08);
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
    }

    &:active {
      transform: scale(0.96);
    }

    svg {
      flex-shrink: 0;
    }
  `}
`;

export const Popover = styled.div<{ $anchor?: 'bottom-right' | 'top-right' }>`
  ${({ theme, $anchor = 'bottom-right' }) => css`
    position: fixed;
    z-index: 1001;
    /* bottom-right (default): floats above the megaphone FAB at the
       page's bottom-right corner.
       top-right: anchored just below the topbar — used by the inline
       mode where the trigger lives in a topbar action cluster. */
    ${$anchor === 'bottom-right'
      ? css`
          bottom: 120px;
          right: 32px;
        `
      : css`
          top: 70px;
          right: 16px;
        `}

    width: 380px;
    background: ${theme.colors.surfaceContainer};
    border: 1px solid ${theme.colors.outlineVariant};
    border-radius: 12px;
    padding: 20px;

    display: flex;
    flex-direction: column;
    gap: 14px;

    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    animation: ${fadeSlideIn} 0.18s ease;
  `}
`;

export const PopoverTitle = styled.p`
  ${({ theme }) => css`
    font-size: ${theme.font.labelLarge.fontSize};
    font-weight: 600;
    color: ${theme.colors.onSurface};
    margin: 0;
  `}
`;

export const PopoverSubtitle = styled.p`
  ${({ theme }) => css`
    font-size: ${theme.font.bodySmall.fontSize};
    color: ${theme.colors.onSurfaceVariant};
    margin: -6px 0 0;
    line-height: 1.5;
  `}
`;

export const FeedbackTextarea = styled.textarea`
  ${({ theme }) => css`
    width: 100%;
    min-height: 140px;
    resize: vertical;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid ${theme.colors.outlineVariant};
    background: ${theme.colors.surfaceContainerLow};
    color: ${theme.colors.onSurface};
    font-family: inherit;
    font-size: ${theme.font.bodyMedium.fontSize};
    line-height: 1.5;
    outline: none;
    transition: border-color 0.15s ease;

    &:focus {
      border-color: ${theme.colors.primary};
    }

    &::placeholder {
      color: ${theme.colors.outline};
    }
  `}
`;

export const SendButton = styled.button<{ $loading?: boolean }>`
  ${({ theme, $loading }) => css`
    width: 100%;
    padding: 11px 18px;
    border: none;
    border-radius: 8px;
    cursor: ${$loading ? 'not-allowed' : 'pointer'};
    background: ${theme.colors.primary};
    color: ${theme.colors.onPrimary};
    font-size: ${theme.font.labelLarge.fontSize};
    font-weight: 600;
    opacity: ${$loading ? 0.7 : 1};
    transition:
      background 0.15s ease,
      opacity 0.15s ease;

    &:hover:not(:disabled) {
      background: ${theme.colors.primaryContainer};
      color: ${theme.colors.onPrimaryContainer};
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  `}
`;

export const ButtonWrapper = styled.div`
  position: fixed;
  bottom: 32px;
  right: 32px;
  z-index: 1000;

  display: none;

  @media (min-width: 769px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }
`;


export const ThanksMessage = styled.p`
  ${({ theme }) => css`
    text-align: center;
    font-size: ${theme.font.bodyMedium.fontSize};
    color: ${theme.colors.onSurface};
    padding: 8px 0;
    margin: 0;
  `}
`;
